import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { TextKey, ActionType } from '@mews-commander/enums';
import { createAction, LocalizedText } from '@mews/framework';
import { Card, ModalBodyWrapper, ModalWrapper, SmallContentContainer } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { ApplicationState, CommanderThunkDispatch, WithGuideLink } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { getEnterpriseName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getServiceIndexUrl } from '@mews-commander/legacy-entities/src/selectors/services';
import { getEnterpriseDetailUrl } from '@mews-commander/legacy-entities/src/selectors/enterprises';
import { fetchAccountingConfiguration } from '@mews-commander/legacy-entities/src/actions/accountingConfigurations';
import { fetchAccountingCategories } from '@mews-commander/legacy-entities/src/actions/accountingCategories';
import { getAccountingCategorySetupRequiredProp } from '@mews-commander/legacy-entities/src/ducks/accountingConfigurations';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import {
    BOOKABLE_SERVICE_FORM_INIT_FAILED,
    BOOKABLE_SERVICE_FORM_INIT_STARTED,
    BOOKABLE_SERVICE_FORM_INIT_SUCCEEDED,
} from '../actions/actionTypes';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import BookableServiceCreateForm from './BookableServiceCreateForm';

export const fetchAccountingConfig = (dispatch: CommanderThunkDispatch, enterpriseId: string) => {
    dispatch(createAction(BOOKABLE_SERVICE_FORM_INIT_STARTED));

    const accountingConfigurationPromise = dispatch(fetchAccountingConfiguration(enterpriseId)).then(
        async (config: { action: unknown; value: unknown }) => {
            // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'null | undefined'.
            const accountingCategorySetupRequired = getAccountingCategorySetupRequiredProp(config.value);
            return accountingCategorySetupRequired === true
                ? dispatch(fetchAccountingCategories({ enterpriseId }))
                : Promise.resolve();
        }
    );

    return accountingConfigurationPromise
        .then(() => dispatch(createAction(BOOKABLE_SERVICE_FORM_INIT_SUCCEEDED)))
        .catch((e: Error) => {
            dispatch(createAction(BOOKABLE_SERVICE_FORM_INIT_FAILED));
            throw e;
        });
};

export const BookableService: React.FC<
    {
        enterpriseId: string;
        form: string;
    } & WithGuideLink
> = ({ enterpriseId, form, guideLink }) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const resolveRoute = useResolveRoute();

    const enterpriseBreadcrumbName = useSelector((state: ApplicationState) =>
        getEnterpriseName(state, { enterpriseId })
    );
    const enterpriseBreadcrumbUrl = useSelector((state: ApplicationState) =>
        getEnterpriseDetailUrl(state, { enterpriseId, resolveRoute })
    );

    const serviceIndexBreadcrumbUrl = useSelector((state: ApplicationState) =>
        getServiceIndexUrl(state, { enterpriseId, resolveRoute })
    );

    useEffect(() => {
        fetchAccountingConfig(dispatch, enterpriseId);
    }, [dispatch, enterpriseId]);

    const breadcrumbs: Breadcrumbs = [
        {
            text: enterpriseBreadcrumbName,
            url: enterpriseBreadcrumbUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.ProductServicePlural} />,
            url: serviceIndexBreadcrumbUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.BookableService} />,
            active: true,
        },
    ];

    const actions: HeadingAction[] = [
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    return (
        <ModalWrapper>
            <ModalHeader
                title={<LocalizedText textKey={TextKey.BookableService} />}
                actions={actions}
                breadcrumbs={breadcrumbs}
            />
            <ModalBodyWrapper padded>
                <SmallContentContainer condensed>
                    <Card>
                        {/* @ts-expect-error $TSFixMeError: JSX element type 'BookableServiceCreateForm' does not have any construct or call signatures. */}
                        <BookableServiceCreateForm enterpriseId={enterpriseId} form={form} />
                    </Card>
                </SmallContentContainer>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
